import { useSelector } from 'react-redux';
import { parseISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { SelectableChip } from '@components/Chip';
import { AiBlock, AiIcon, AiText } from '@components/AiButton';
import { Typography } from '@components/Typography';
import { formatDateOrEmpty } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { AI_KIND, aiKindFor } from './recommendations';
import { ASSIST_STATUS, useScoreAssistant } from './ScoreAssistantProvider';

const Block = styled(AiBlock)({
    margin: '0 8px 8px 40px',
});

const Head = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
});

const HeadText = styled(AiText)({
    fontSize: 12,
    lineHeight: '14px',
    fontWeight: 500,
    letterSpacing: '0.4px',
});

const Chips = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
});

const Holidays = styled('div')({
    marginBottom: 10,
});

const Holiday = styled('label')({
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr) max-content',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    '& .MuiCheckbox-root': {
        padding: 4,
        marginLeft: -4,
    },
});

const Draft = styled(Typography)({
    color: 'var(--text-primary)',
    marginBottom: 12,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
});

const Actions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const Status = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: 'var(--text-secondary)',
});

const HEAD_KEY = {
    [AI_KIND.SERVICES]: 'ScorePanel.aiServices',
    [AI_KIND.CATEGORIES]: 'ScorePanel.aiCategories',
    [AI_KIND.SPECIAL_HOURS]: 'ScorePanel.aiSpecialHours',
    [AI_KIND.DESCRIPTION]: 'ScorePanel.aiDescription',
};

const APPLY_KEY = {
    [AI_KIND.SERVICES]: 'ScorePanel.addServices',
    [AI_KIND.CATEGORIES]: 'ScorePanel.addCategories',
    [AI_KIND.SPECIAL_HOURS]: 'ScorePanel.addDates',
};

const SuggestionBlock = ({ recommendationKey, country }) => {
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { assist, isLocked, toggle, apply, dismiss, suggestionView } = useScoreAssistant();
    const entry = assist[recommendationKey];
    const kind = aiKindFor(recommendationKey);

    if (!entry) return null;

    if (entry.status === ASSIST_STATUS.LOADING) {
        return (
            <Block>
                <Status role="status">
                    <CircularProgress size={16} sx={{ color: 'var(--text-tertiary)' }} />
                    <Typography variant="caption">{t('ScorePanel.thinking')}</Typography>
                </Status>
            </Block>
        );
    }

    const view =
        entry.status === ASSIST_STATUS.READY && kind !== AI_KIND.DESCRIPTION ? suggestionView(recommendationKey) : null;

    if (entry.status !== ASSIST_STATUS.READY || view?.items.length === 0) {
        return (
            <Block>
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '10px' }}
                >
                    {entry.status === ASSIST_STATUS.ERROR
                        ? entry.message || t('ScorePanel.failed')
                        : t('ScorePanel.noSuggestions')}
                </Typography>
                <Actions>
                    <Button variant="text" onClick={() => dismiss(recommendationKey)}>
                        {t('ScorePanel.dismiss')}
                    </Button>
                </Actions>
            </Block>
        );
    }

    const chosenCount = view?.chosen.length ?? 0;
    const atLimit = chosenCount >= view?.limit;

    return (
        <Block>
            <Head>
                <AiIcon />
                <HeadText>{t(HEAD_KEY[kind], { country })}</HeadText>
            </Head>
            {kind === AI_KIND.DESCRIPTION && <Draft variant="body2">{entry.draft}</Draft>}
            {(kind === AI_KIND.SERVICES || kind === AI_KIND.CATEGORIES) && (
                <Chips>
                    {view.items.map(item => {
                        const selected = view.chosen.includes(item);
                        return (
                            <SelectableChip
                                key={item.id ?? item}
                                label={item.displayName ?? item}
                                selected={selected}
                                checkmark
                                disabled={!selected && atLimit}
                                onClick={() => toggle(recommendationKey, item)}
                            />
                        );
                    })}
                </Chips>
            )}
            {kind === AI_KIND.SPECIAL_HOURS && (
                <>
                    <Holidays>
                        {view.items.map(item => (
                            <Holiday key={item.date}>
                                <Checkbox
                                    checked={view.chosen.includes(item)}
                                    onChange={() => toggle(recommendationKey, item)}
                                />
                                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                                    {item.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {formatDateOrEmpty(parseISO(item.date), 'EEE, d MMM', language)}
                                </Typography>
                            </Holiday>
                        ))}
                    </Holidays>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}
                    >
                        {t('ScorePanel.specialHoursNote')}
                    </Typography>
                </>
            )}
            <Actions>
                {kind === AI_KIND.DESCRIPTION ? (
                    <Button variant="contained" disabled={isLocked} onClick={() => apply(recommendationKey)}>
                        {t('ScorePanel.replace')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        disabled={isLocked || chosenCount === 0}
                        onClick={() => apply(recommendationKey)}
                    >
                        {t(APPLY_KEY[kind], { count: chosenCount })}
                    </Button>
                )}
                <Button variant="text" onClick={() => dismiss(recommendationKey)}>
                    {t('ScorePanel.dismiss')}
                </Button>
            </Actions>
        </Block>
    );
};

export default SuggestionBlock;
